import { useMemo } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import type { LocationCacheItem } from "../../types/session.ts";
import { IdentitySinglePicker, type IdentitySinglePickerConfig } from "./identitySinglePicker.tsx";

interface LocationSinglePickerProps {
  value: number;
  onChange: (id: number) => void;
  disabled?: boolean;
  /** Override search-input placeholder (defaults to the current location name). */
  placeholder?: string;
  /** Called when user presses Enter inside the ID input. */
  onIdEnter?: () => void;
}

/**
 * Form field: pick a single location by ID or by typing (StoreName / Address /
 * City / State / Zip / Chain). Reads from `cache.CommonData.Location` via
 * `useAuth`. Use anywhere a form needs a single LocationID — replaces the
 * legacy "select dropdown over thousands of entries" pattern.
 */
export function LocationSinglePicker({ value, onChange, disabled, placeholder, onIdEnter }: LocationSinglePickerProps) {
  const { cache } = useAuth();
  const items = cache?.CommonData?.Location ?? [];

  const config = useMemo<IdentitySinglePickerConfig<LocationCacheItem>>(
    () => ({
      items,
      getId: (l) => l.LocationID,
      nameField: "StoreName",
      noun: "location",
      matchFn: (l, q) =>
        String(l.LocationID).includes(q) ||
        (!!l.StoreName && l.StoreName.toLowerCase().includes(q)) ||
        (!!l.Address && l.Address.toLowerCase().includes(q)) ||
        (!!l.City && l.City.toLowerCase().includes(q)) ||
        (!!l.District && l.District.toLowerCase().includes(q)) ||
        (!!l.ZipCode && l.ZipCode.toLowerCase().includes(q)) ||
        (!!l.Country && l.Country.toLowerCase().includes(q)) ||
        (!!l.Chain && l.Chain.toLowerCase().includes(q)) ||
        (!!l.ShortName && l.ShortName.toLowerCase().includes(q)),
      secondaryFn: (l) =>
        [l.Address, l.City, l.District, l.ZipCode, l.Country].filter(Boolean).join(", "),
    }),
    [items],
  );

  return (
    <IdentitySinglePicker
      value={value}
      onChange={onChange}
      config={config}
      placeholder={placeholder}
      disabled={disabled}
      onIdEnter={onIdEnter}
    />
  );
}
